/**
 * Main App Component
 *
 * Defines all application routes including:
 * - User authentication routes
 * - Doctor authentication routes
 * - Video call routes (waiting room + video call)
 * - Protected routes with auth checks
 */

import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { initAuthSyncListener } from './utils/auth';
import { UserProtectedRoute, DoctorProtectedRoute, AdminProtectedRoute } from './components/ProtectedRoute';
import Home from './pages/Home';
import UserRegister from './pages/UserRegister';
import UserLogin from './pages/UserLogin';
import ForgotPassword from './pages/ForgotPassword';
import DoctorRegister from './pages/DoctorRegister';
import DoctorLogin from './pages/DoctorLogin';
import UserDashboard from './pages/UserDashboard';
import DoctorDashboard from './pages/DoctorDashboard';
import UserProfile from './pages/UserProfile';
import DoctorProfile from './pages/DoctorProfile';
import DoctorCreatePost from './pages/DoctorCreatePost';
import DoctorList from './pages/DoctorList';
import BookAppointment from './pages/BookAppointment';
import UserBookings from './pages/UserBookings';
import UserFavorites from './pages/UserFavorites';
import UserFamily from './pages/UserFamily';
import DoctorProfileView from './pages/DoctorProfileView';
import SocialFeed from './pages/SocialFeed';
import UserNotifications from './pages/UserNotifications';
import UserSettings from './pages/UserSettings';
import PostDetail from './pages/PostDetail';
import DoctorNotifications from './pages/DoctorNotifications';
import DoctorSettings from './pages/DoctorSettings';
import DoctorBankDetails from './pages/DoctorBankDetails';
import DoctorEarningsHistory from './pages/DoctorEarningsHistory';
import DoctorPosts from './pages/DoctorPosts';
import DoctorAppointments from './pages/DoctorAppointments';
import VideoCall from './pages/VideoCall';
import WaitingRoom from './pages/WaitingRoom';
import Feedback from './pages/Feedback';
import PaymentPage from './pages/payment/PaymentPage';
import PaymentSuccess from './pages/payment/PaymentSuccess';
import PaymentFailed from './pages/payment/PaymentFailed';
import BookingSummary from './pages/BookingSummary';
import UserPaymentHistory from './pages/UserPaymentHistory';
import UserSavedCards from './pages/UserSavedCards';
import ChatPage from './pages/ChatPage';
import UserHealthRecords from './pages/UserHealthRecords';
import AdminLogin from './pages/admin/AdminLogin';
import AdminForgotPassword from './pages/admin/AdminForgotPassword';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminUserDetail from './pages/admin/AdminUserDetail';
import AdminDoctors from './pages/admin/AdminDoctors';
import AdminDoctorDetail from './pages/admin/AdminDoctorDetail';
import AdminDoctorReviews from './pages/admin/AdminDoctorReviews';
import AdminAppointments from './pages/admin/AdminAppointments';
import AdminPayments from './pages/admin/AdminPayments';
import AdminPrescriptions from './pages/admin/AdminPrescriptions';
import AdminHealthDocuments from './pages/admin/AdminHealthDocuments';
import AdminPosts from './pages/admin/AdminPosts';
import AdminPostDetail from './pages/admin/AdminPostDetail';
import AdminRatings from './pages/admin/AdminRatings';
import AdminHealthContent from './pages/admin/AdminHealthContent';
import AdminEarnings from './pages/admin/AdminEarnings';
import AdminNotifications from './pages/admin/AdminNotifications';
import AdminProfile from './pages/admin/AdminProfile';

function App() {
  useEffect(() => {
    initAuthSyncListener();
    const onExpired = (e) => {
      const message = e.detail?.message;
      if (message) toast.info(message, { autoClose: 5000 });
    };
    window.addEventListener('fitnova:session-expired', onExpired);
    return () => window.removeEventListener('fitnova:session-expired', onExpired);
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/user/register" element={<UserRegister />} />
        <Route path="/user/login" element={<UserLogin />} />
        <Route path="/user/forgot-password" element={<ForgotPassword />} />
        <Route path="/doctor/register" element={<DoctorRegister />} />
        <Route path="/doctor/login" element={<DoctorLogin />} />
        <Route path="/doctor/forgot-password" element={<ForgotPassword />} />

        <Route path="/user/dashboard" element={<UserProtectedRoute><UserDashboard /></UserProtectedRoute>} />
        <Route path="/user/doctor_list" element={<UserProtectedRoute><DoctorList /></UserProtectedRoute>} />
        <Route path="/user/book-appointment/:doctorId" element={<UserProtectedRoute><BookAppointment /></UserProtectedRoute>} />
        <Route path="/user/doctor-profile/:doctorId" element={<UserProtectedRoute><DoctorProfileView /></UserProtectedRoute>} />
        <Route path="/user/bookings" element={<UserProtectedRoute><UserBookings /></UserProtectedRoute>} />
        <Route path="/user/favorites" element={<UserProtectedRoute><UserFavorites /></UserProtectedRoute>} />
        <Route path="/user/family" element={<UserProtectedRoute><UserFamily /></UserProtectedRoute>} />
        <Route path="/user/profile" element={<UserProtectedRoute><UserProfile /></UserProtectedRoute>} />
        <Route path="/user/feed" element={<UserProtectedRoute><SocialFeed /></UserProtectedRoute>} />
        <Route path="/user/notifications" element={<UserProtectedRoute><UserNotifications /></UserProtectedRoute>} />
        <Route path="/user/settings" element={<UserProtectedRoute><UserSettings /></UserProtectedRoute>} />
        <Route path="/user/payment-history" element={<UserProtectedRoute><UserPaymentHistory /></UserProtectedRoute>} />
        <Route path="/user/saved-cards" element={<UserProtectedRoute><UserSavedCards /></UserProtectedRoute>} />
        <Route path="/user/privacy" element={<UserProtectedRoute><UserSettings /></UserProtectedRoute>} />
        <Route path="/user/policy" element={<UserProtectedRoute><UserSettings /></UserProtectedRoute>} />
        <Route path="/user/video-call/:appointmentId" element={<UserProtectedRoute><VideoCall /></UserProtectedRoute>} />
        <Route path="/user/waiting-room/:appointmentId" element={<UserProtectedRoute><WaitingRoom /></UserProtectedRoute>} />
        <Route path="/feedback/:appointmentId" element={<UserProtectedRoute><Feedback /></UserProtectedRoute>} />
        <Route path="/user/booking-summary" element={<UserProtectedRoute><BookingSummary /></UserProtectedRoute>} />
        <Route path="/user/payment" element={<UserProtectedRoute><PaymentPage /></UserProtectedRoute>} />
        <Route path="/user/payment-success" element={<UserProtectedRoute><PaymentSuccess /></UserProtectedRoute>} />
        <Route path="/user/payment-failed" element={<UserProtectedRoute><PaymentFailed /></UserProtectedRoute>} />
        <Route path="/user/chat" element={<UserProtectedRoute><ChatPage /></UserProtectedRoute>} />
        <Route path="/user/health-records" element={<UserProtectedRoute><UserHealthRecords /></UserProtectedRoute>} />

        <Route path="/doctor/dashboard" element={<DoctorProtectedRoute><DoctorDashboard /></DoctorProtectedRoute>} />
        <Route path="/doctor/create-post" element={<DoctorProtectedRoute><DoctorCreatePost /></DoctorProtectedRoute>} />
        <Route path="/doctor/profile" element={<DoctorProtectedRoute><DoctorProfile /></DoctorProtectedRoute>} />
        <Route path="/doctor/notifications" element={<DoctorProtectedRoute><DoctorNotifications /></DoctorProtectedRoute>} />
        <Route path="/doctor/settings" element={<DoctorProtectedRoute><DoctorSettings /></DoctorProtectedRoute>} />
        <Route path="/doctor/bank-details" element={<DoctorProtectedRoute><DoctorBankDetails /></DoctorProtectedRoute>} />
        <Route path="/doctor/earnings" element={<DoctorProtectedRoute><DoctorEarningsHistory /></DoctorProtectedRoute>} />
        <Route path="/doctor/posts" element={<DoctorProtectedRoute><DoctorPosts /></DoctorProtectedRoute>} />
        <Route path="/doctor/appointments" element={<DoctorProtectedRoute><DoctorAppointments /></DoctorProtectedRoute>} />
        <Route path="/doctor/video-call/:appointmentId" element={<DoctorProtectedRoute><VideoCall /></DoctorProtectedRoute>} />
        <Route path="/doctor/waiting-room/:appointmentId" element={<DoctorProtectedRoute><WaitingRoom /></DoctorProtectedRoute>} />
        <Route path="/doctor/privacy" element={<DoctorProtectedRoute><DoctorSettings /></DoctorProtectedRoute>} />
        <Route path="/doctor/policy" element={<DoctorProtectedRoute><DoctorSettings /></DoctorProtectedRoute>} />

        <Route path="/post/:id" element={<PostDetail />} />

        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
        <Route
          path="/admin"
          element={
            <AdminProtectedRoute>
              <AdminLayout />
            </AdminProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="profile" element={<AdminProfile />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="users/:userId" element={<AdminUserDetail />} />
          <Route path="doctors" element={<AdminDoctors />} />
          <Route path="doctors/:doctorId/reviews" element={<AdminDoctorReviews />} />
          <Route path="doctors/:doctorId" element={<AdminDoctorDetail />} />
          <Route path="appointments" element={<AdminAppointments />} />
          <Route path="payments" element={<AdminPayments />} />
          <Route path="earnings" element={<AdminEarnings />} />
          <Route path="notifications" element={<AdminNotifications />} />
          <Route path="prescriptions" element={<AdminPrescriptions />} />
          <Route path="health-documents" element={<AdminHealthDocuments />} />
          <Route path="posts" element={<AdminPosts />} />
          <Route path="posts/:postId" element={<AdminPostDetail />} />
          <Route path="ratings" element={<AdminRatings />} />
          <Route path="health-content" element={<AdminHealthContent />} />
        </Route>

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
        theme="colored"
        style={{ zIndex: 99999 }}
      />
    </BrowserRouter>
  );
}
export default App;
